import { useState } from "react";
import { Modal, TextInput, Stack, Text, UnstyledButton, Loader, Group } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props<T> {
    opened: boolean;
    onClose: () => void;
    title: string;
    /** Returns the candidate list, optionally filtered by `q`. */
    loadCandidates: (q: string) => Promise<T[]>;
    /** Already linked ids — these rows are hidden from the picker. */
    excludeIds: number[];
    /** Render label for one candidate row. */
    labelOf: (item: T) => string;
    idOf: (item: T) => number;
    onPick: (id: number) => Promise<void>;
}

export function LinkPickerModal<T>({
    opened,
    onClose,
    title,
    loadCandidates,
    excludeIds,
    labelOf,
    idOf,
    onPick,
}: Props<T>) {
    const { t } = useTranslation();
    const [q, setQ] = useState("");
    const [debouncedQ] = useDebouncedValue(q, 200);
    const [picking, setPicking] = useState<number | null>(null);

    const candidatesQuery = useQuery({
        queryKey: ["linkpicker-candidates", title, debouncedQ],
        queryFn: () => loadCandidates(debouncedQ),
        enabled: opened,
        staleTime: 10_000,
    });

    const excludeSet = new Set(excludeIds);
    const visible = (candidatesQuery.data ?? []).filter((item) => !excludeSet.has(idOf(item)));

    const handlePick = async (id: number) => {
        setPicking(id);
        try {
            await onPick(id);
            onClose();
        } finally {
            setPicking(null);
        }
    };

    const handleClose = () => {
        setQ("");
        onClose();
    };

    return (
        <Modal opened={opened} onClose={handleClose} title={title} size="md">
            <Stack gap="xs">
                <TextInput
                    placeholder={t("common.search.placeholder")}
                    value={q}
                    onChange={(e) => setQ(e.currentTarget.value)}
                    autoFocus
                />
                {candidatesQuery.isLoading && (
                    <Group justify="center" py="sm">
                        <Loader size="sm" />
                    </Group>
                )}
                {!candidatesQuery.isLoading && visible.length === 0 && (
                    <Text c="dimmed" size="sm" ta="center" py="sm">
                        {t("common.search.noResults")}
                    </Text>
                )}
                <Stack gap={4}>
                    {visible.map((item) => {
                        const id = idOf(item);
                        const isPickingThis = picking === id;
                        return (
                            <UnstyledButton
                                key={id}
                                onClick={() => handlePick(id)}
                                disabled={picking !== null}
                                style={{
                                    padding: "8px 12px",
                                    borderRadius: 6,
                                    border: "1px solid var(--mantine-color-default-border)",
                                    opacity: picking !== null && !isPickingThis ? 0.5 : 1,
                                    cursor: picking !== null ? "default" : "pointer",
                                }}
                            >
                                <Group gap="xs">
                                    {isPickingThis && <Loader size="xs" />}
                                    <Text size="sm">{labelOf(item)}</Text>
                                </Group>
                            </UnstyledButton>
                        );
                    })}
                </Stack>
            </Stack>
        </Modal>
    );
}
